import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { useCart } from "../context/CartContext";
import { createOrder } from "../firebase/orderService";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  background: #0a0a0a;
  font-family: "Georgia", serif;
  padding: 100px 64px 80px;
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 80px;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    padding: 100px 24px 60px;
    gap: 48px;
  }
`;

const Left = styled.div`
  animation: ${fadeUp} 0.5s ease forwards;
`;

const Right = styled.div`
  animation: ${fadeUp} 0.5s ease 0.1s forwards;
  opacity: 0;
`;

const PageTitle = styled.h1`
  color: #fff;
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  font-weight: 400;
  letter-spacing: 0.04em;
  margin: 0 0 8px 0;
`;

const PageSub = styled.p`
  color: #444;
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin: 0 0 48px 0;
`;

const SectionLabel = styled.p`
  color: #444;
  font-size: 0.65rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #1a1a1a;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$cols || "1fr"};
  gap: 16px;
  margin-bottom: 16px;
`;

const Field = styled.div``;

const Label = styled.label`
  display: block;
  color: #555;
  font-size: 0.65rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  background: #111;
  border: 1px solid #1e1e1e;
  color: #fff;
  padding: 13px 16px;
  font-size: 0.9rem;
  font-family: "Georgia", serif;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    border-color: #444;
  }
  &::placeholder {
    color: #2a2a2a;
  }
`;

const Select = styled.select`
  width: 100%;
  background: #111;
  border: 1px solid #1e1e1e;
  color: #fff;
  padding: 13px 16px;
  font-size: 0.9rem;
  font-family: "Georgia", serif;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;
  cursor: pointer;

  &:focus {
    border-color: #444;
  }
  option {
    background: #111;
  }
`;

const SubmitBtn = styled.button`
  background: #fff;
  color: #0a0a0a;
  border: none;
  padding: 16px 40px;
  font-size: 0.75rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  cursor: pointer;
  font-family: "Georgia", serif;
  margin-top: 32px;
  transition: background 0.2s;

  &:hover {
    background: #ddd;
  }
  &:disabled {
    background: #222;
    color: #555;
    cursor: not-allowed;
  }
`;

const ErrorMsg = styled.p`
  color: #c0392b;
  font-size: 0.75rem;
  margin: 16px 0 0 0;
  letter-spacing: 0.05em;
`;

// ── Order Summary ──────────────────────────────────────────────────────────────
const SummaryCard = styled.div`
  background: #111;
  border: 1px solid #1e1e1e;
  padding: 28px;
  position: sticky;
  top: 100px;
`;

const SummaryTitle = styled.p`
  color: #444;
  font-size: 0.65rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin: 0 0 24px 0;
  padding-bottom: 16px;
  border-bottom: 1px solid #1a1a1a;
`;

const SummaryItem = styled.div`
  display: flex;
  gap: 14px;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #161616;

  &:last-of-type {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const SummaryImage = styled.div`
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  background: #0a0a0a;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const SummaryInfo = styled.div`
  flex: 1;
`;

const SummaryName = styled.p`
  color: #ccc;
  font-size: 0.85rem;
  margin: 0 0 4px 0;
`;

const SummaryMeta = styled.p`
  color: #444;
  font-size: 0.65rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0;
`;

const SummaryPrice = styled.p`
  color: #888;
  font-size: 0.82rem;
  margin: 4px 0 0 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #1e1e1e;
`;

const TotalLabel = styled.span`
  color: #555;
  font-size: 0.68rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const TotalValue = styled.span`
  color: #fff;
  font-size: 1.1rem;
`;

const COUNTRIES = [
  "Albania",
  "Austria",
  "Belgium",
  "Bosnia and Herzegovina",
  "Bulgaria",
  "Croatia",
  "Cyprus",
  "Czech Republic",
  "Denmark",
  "Estonia",
  "Finland",
  "France",
  "Germany",
  "Greece",
  "Hungary",
  "Ireland",
  "Italy",
  "Kosovo",
  "Latvia",
  "Lithuania",
  "Luxembourg",
  "Malta",
  "Montenegro",
  "Netherlands",
  "North Macedonia",
  "Norway",
  "Poland",
  "Portugal",
  "Romania",
  "Serbia",
  "Slovakia",
  "Slovenia",
  "Spain",
  "Sweden",
  "Switzerland",
  "United Kingdom",
  "Other",
];

const Checkout = () => {
  const { items, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    address: "",
    city: "",
    postalCode: "",
    country: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleField = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
  };

  const validate = () => {
    const required = [
      "firstName",
      "lastName",
      "email",
      "address",
      "city",
      "postalCode",
      "country",
    ];
    for (const key of required) {
      if (!form[key].trim()) return `Please fill in all fields.`;
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) return "Please enter a valid email.";
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    if (items.length === 0) {
      setError("Your cart is empty.");
      return;
    }

    setSubmitting(true);
    try {
      const orderData = {
        items: items.map((item) => ({
          productId: item.productId,
          name: item.name,
          frameType: item.frameType,
          size: item.size,
          price: item.price,
          quantity: item.quantity,
          imageUrl: item.imageUrl || null,
          category: item.category || null,
          // Custom print fields — only present on custom items
          isCustom: item.isCustom || false,
          originalUrl: item.originalUrl || null,
          publicId: item.publicId || null,
          imageWidth: item.imageWidth || null,
          imageHeight: item.imageHeight || null,
          imageFormat: item.imageFormat || null,
          imageBytes: item.imageBytes || null,
        })),
        // For backward compat with admin panel (single product view):
        product:
          items.length === 1
            ? {
                id: items[0].productId,
                name: items[0].name,
                frameType: items[0].frameType,
                size: items[0].size,
                price: items[0].price,
              }
            : { name: `${items.length} items`, price: totalPrice },
        shipping: {
          name: `${form.firstName} ${form.lastName}`,
          email: form.email,
          address: form.address,
          city: form.city,
          postalCode: form.postalCode,
          country: form.country,
        },
        total: totalPrice,
        status: "pending",
      };

      const orderId = await createOrder(orderData);
      clearCart();
      navigate(`/order-success/${orderId}`);
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0 && !submitting) {
    return (
      <Page
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gridTemplateColumns: "1fr",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <p
            style={{
              color: "#333",
              fontSize: "0.8rem",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              marginBottom: "24px",
            }}
          >
            Your cart is empty
          </p>
          <button
            onClick={() => navigate("/shop")}
            style={{
              background: "none",
              border: "1px solid #333",
              color: "#666",
              padding: "10px 24px",
              fontSize: "0.7rem",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              cursor: "pointer",
              fontFamily: "Georgia, serif",
            }}
          >
            Go to Shop
          </button>
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <Left>
        <PageTitle>Checkout</PageTitle>
        <PageSub>Shipping Information</PageSub>

        <SectionLabel>Contact</SectionLabel>
        <FormGrid>
          <Field>
            <Label>Email</Label>
            <Input
              name="email"
              type="email"
              value={form.email}
              onChange={handleField}
              placeholder="your@email.com"
            />
          </Field>
        </FormGrid>

        <SectionLabel style={{ marginTop: "32px" }}>
          Shipping Address
        </SectionLabel>
        <FormGrid $cols="1fr 1fr">
          <Field>
            <Label>First Name</Label>
            <Input
              name="firstName"
              value={form.firstName}
              onChange={handleField}
              placeholder="John"
            />
          </Field>
          <Field>
            <Label>Last Name</Label>
            <Input
              name="lastName"
              value={form.lastName}
              onChange={handleField}
              placeholder="Doe"
            />
          </Field>
        </FormGrid>
        <FormGrid>
          <Field>
            <Label>Address</Label>
            <Input
              name="address"
              value={form.address}
              onChange={handleField}
              placeholder="Street and number"
            />
          </Field>
        </FormGrid>
        <FormGrid $cols="1fr 1fr 1fr">
          <Field>
            <Label>City</Label>
            <Input
              name="city"
              value={form.city}
              onChange={handleField}
              placeholder="City"
            />
          </Field>
          <Field>
            <Label>Postal Code</Label>
            <Input
              name="postalCode"
              value={form.postalCode}
              onChange={handleField}
              placeholder="10000"
            />
          </Field>
          <Field>
            <Label>Country</Label>
            <Select name="country" value={form.country} onChange={handleField}>
              <option value="">Select...</option>
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
        </FormGrid>

        {error && <ErrorMsg>{error}</ErrorMsg>}

        <SubmitBtn onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Placing Order..." : "Place Order"}
        </SubmitBtn>
      </Left>

      <Right>
        <SummaryCard>
          <SummaryTitle>Order Summary</SummaryTitle>
          {items.map((item) => (
            <SummaryItem key={item.cartId}>
              <SummaryImage>
                {item.imageUrl && <img src={item.imageUrl} alt={item.name} />}
              </SummaryImage>
              <SummaryInfo>
                <SummaryName>{item.name}</SummaryName>
                <SummaryMeta>
                  {item.frameType} · {item.size}
                </SummaryMeta>
                <SummaryPrice>
                  €{(item.price * item.quantity).toFixed(2)}{" "}
                  {item.quantity > 1 && `× ${item.quantity}`}
                </SummaryPrice>
              </SummaryInfo>
            </SummaryItem>
          ))}
          <TotalRow>
            <TotalLabel>Total</TotalLabel>
            <TotalValue>€{totalPrice.toFixed(2)}</TotalValue>
          </TotalRow>
        </SummaryCard>
      </Right>
    </Page>
  );
};

export default Checkout;
